import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, LinearProgress } from "@mui/material";
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useEffect, useState } from "react";
import { IExternalDevice } from "models/IExternalDevice";

export type IUploadExternalDeviceDataDialogProps = {
    openDialog: boolean;
    externalDevice: IExternalDevice;
    handleClose: () => void;
    handleUploadExternalDeviceData: (externalDeviceId: string, file: File) => void;
    uploadProgress: number | null;
};

export default function UploadExternalDeviceDataDialog({ openDialog, externalDevice, handleClose, handleUploadExternalDeviceData, uploadProgress }: IUploadExternalDeviceDataDialogProps) {
    const { t } = useTranslation();
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [dots, setDots] = useState<string>("");

    const handleAddDialogClose: (event: any, reason: any) => void = (event, reason) => {
        if (reason && reason === "backdropClick") {
            return;
        }

        handleClose();
        setUploading(false);
        setUploadedFile(null);
    };

    const validationSchema = yup.object({
        id: yup.string().required(t('field_required', 'This field is required.')!!),
        file: yup.mixed().required(t('file_required', 'A CSV file is required.')!!)
    });

    const formik = useFormik({
        initialValues: {
            id: externalDevice.id!!,
            file: uploadedFile
        },
        validationSchema,
        onSubmit: (values) => {
            setUploading(true);
            handleUploadExternalDeviceData(values.id, uploadedFile!!);
        }
    });

    const onDrop = (acceptedFiles: File[]) => {
        const file = acceptedFiles.find((file) => file.type === "text/csv");
        if (file) {
            setUploadedFile(file);
            formik.setFieldValue("file", file);
        } else {
            alert(t('invalid_file_type', 'Invalid file type. Please upload a CSV file.'));
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false
    });

    useEffect(() => {
        if (!uploading) return;

        const interval = setInterval(() => {
            setDots(prev => (prev.length >= 3 ? "" : prev + "."));
        }, 500);

        return () => clearInterval(interval);
    }, [uploading]);

    return (
        <Dialog
            open={openDialog}
            onClose={handleAddDialogClose}
            fullWidth
            maxWidth="sm"
            sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: '600px' } }}
        >
            <form noValidate onSubmit={formik.handleSubmit}>
                <DialogTitle>{t('upload_file', 'Upload file')}</DialogTitle>
                <DialogContent>
                    {!uploading && (
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed #ccc',
                                borderRadius: '4px',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: isDragActive ? '#f0f0f0' : '#fafafa',
                                width: '100%',
                                height: '150px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <input {...getInputProps()} />
                            {uploadedFile ? (
                                <Box>
                                    <Typography>{uploadedFile.name}</Typography>
                                    <Typography color="textSecondary">
                                    {`${(uploadedFile.size / 1000 < 1000)
                                        ? `${(uploadedFile.size / 1000).toFixed(2)} KB`
                                        : `${(uploadedFile.size / (1000 * 1000)).toFixed(2)} MB`
                                    }`}
                                    </Typography>
                                </Box>
                                ) : (
                                <Typography>
                                    {isDragActive
                                    ? t('drop_file', 'Drop the file here...')
                                    : t('drag_drop_or_click', 'Drag and drop a CSV file here, or click to select one.')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {uploading && uploadProgress !== null && (
                        <Box sx={{ mt: 2 }}>
                            <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography>
                                    {t('uploading_file', 'Uploading file')}{dots}
                                </Typography>
                                <Typography>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 10, borderRadius: 5 }}/>
                        </Box>
                    )}

                    {!uploading && formik.errors.file && formik.touched.file && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {formik.errors.file}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleAddDialogClose(null, null)} variant="outlined" color="secondary">{t('cancel', 'Cancel')}</Button>
                    <Button type="submit" variant="contained" disabled={uploading}>{t('save', 'Save')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}